// Website analytics events (2026-11-03). posthog-js is set up in
// instrumentation-client.ts; where it isn't (local dev, unknown hosts) these
// calls do nothing. Never pass anything a visitor typed: test codes, scores,
// counts and our own enums only.
import posthog, { type CaptureResult } from "posthog-js";
import { embedAttribution, isEmbedPage } from "@/lib/embed";

type Props = Record<string, string | number | boolean | null | undefined>;
let embedAnalyticsReady = false;

/** The SDK can add session-entry URLs after capture(). Scrub the final event,
 * including automatic metadata, while preserving any existing consent hook. */
function scrubEmbedEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event || !isEmbedPage()) return event;
  const safe: CaptureResult = { ...event, properties: { ...event.properties, ...embedAttribution() } };
  for (const key of Object.keys(safe.properties)) {
    if (key !== "$sentry_url" && /referr|url|pathname|search|utm_|campaign/i.test(key)) delete safe.properties[key];
  }
  delete safe.$set;
  delete safe.$set_once;
  return safe;
}

/** Send one event. `beacon`: the page may be about to unload (a same-tab link). */
export function track(event: string, props: Props, beacon = false): void {
  try {
    if (!(posthog as unknown as { __loaded?: boolean }).__loaded) return;
    if (isEmbedPage()) {
      // Ephemeral analytics only in frames; blocked storage is normal. Never
      // inherit an account identity or record the host's full referring URL.
      if (!embedAnalyticsReady) {
        const before = posthog.config.before_send;
        const hooks = Array.isArray(before) ? before : before ? [before] : [];
        posthog.set_config({
          persistence: "memory", disable_persistence: true,
          autocapture: false, capture_pageview: false, capture_pageleave: false,
          disable_session_recording: true, person_profiles: "never",
          save_referrer: false, save_campaign_params: false,
          property_denylist: [...(posthog.config.property_denylist || []),
            "$initial_referrer", "$initial_referrer_info", "$initial_person_info",
            "$initial_campaign_params", "$initial_current_url"],
          before_send: [scrubEmbedEvent, ...hooks, scrubEmbedEvent],
        });
        posthog.reset(true);
        embedAnalyticsReady = true;
      }
      posthog.capture(event, { ...props, ...embedAttribution(), $referrer: "", $referring_domain: embedAttribution().embed_host ?? "" }, { $set: {}, $set_once: {} });
    } else {
      posthog.capture(event, props, beacon ? { transport: "sendBeacon" } : undefined);
    }
  } catch {
    // analytics must never break a page
  }
}
